import { IFirebase } from './IFirebase'
import {
  genUID, templateString
} from './Utils';
import {
  initializeApp
} from 'firebase/app';
import {
  getFirestore,
  getDocs,
  getDoc,
  doc,
  setDoc,
  collection,
  where,
  query,
  QueryConstraint
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged, setPersistence } from 'firebase/auth';
import firebase from "firebase/compat";
import WhereFilterOp = firebase.firestore.WhereFilterOp;

/*
events to listen for:
ready - when firebase instance is ready
sessionupdate - when firebase user state updates
*/

export interface RequestFilterCondition { fieldName: string, operator: WhereFilterOp, value: any }

export class Firebase implements IFirebase {
  _fbApp = null;
  _auth = null;
  _firestore = null;
  _listeners = {};
  _config: any = {};
  _isLoggedIn = false;
  _user = null;
  _initialized = false;

  constructor(public config: any = {}) {
    this.setConfig(config);
  }

  setConfig(config: any = {}): void {
    this._config = {
      firebase: {},
      defaultWriteOptions: {
        merge: true
      },
      ...config
    };
  }

  public attach(name: string, callback: any): string {
    let eventId = genUID();

    this._listeners[eventId] = {
      name,
      callback
    }

    return eventId;
  }

  public detach(id: string): void {
    delete this._listeners[id];
  }

  private fire(eventName: string, ...argx: any[]): void {
    for (let k in this._listeners) {
      let listener = this._listeners[k];

      if (listener.name == eventName) {
        listener.callback(...argx);
      }
    }
  }

  public async initialize(): Promise<void> {
    if (!this._fbApp)
      this._fbApp = initializeApp(this._config);

    if (!this._auth) {
      this._auth = getAuth(this._fbApp);

      onAuthStateChanged(this._auth, this.onAuthStateChanged.bind(this));
      // await this.updateAuthState();
    }

    if (!this._firestore) {
      this._firestore = getFirestore(this._fbApp);
    }
  }

  public async updateAuthState(): Promise<void> {
    if (this._auth) {
      let user = this._auth.currentUser;

      await this.onAuthStateChanged(user);
    }
  }

  private async onAuthStateChanged(user: any): Promise<void> {
    console.log('ExerciserSDK:Firebase:onAuthStateChanged', user);
    if (user) {
      this._isLoggedIn = true;
      this._user = user;
    } else {
      this._isLoggedIn = false;
      this._user = null;
    }

    if (!this._initialized) {
      this._initialized = true;
      this.fire('initialized');
    }

    this.fire('userchange', this._user);
  }

  public isLoggedIn(): boolean {
    return this._isLoggedIn;
  }

  public getUserAuth(): any {
    return this._user || { notLoggedIn: true };
  }

  private compilePath(path: string, keys = {}): string[] {
    return templateString(path, {
      userId: this.getUserAuth().uid,
      ...keys
    }).split('/')
  }


  private compilePathToString(path: string, keys = {}): string {
    return templateString(path, {
      userId: this.getUserAuth().uid,
      ...keys
    })
  }

  public async read({ path, keys }): Promise<any> {
    let ref = doc(this._firestore, ...this.compilePath(path, keys));
    let snap = await getDoc(ref);

    if (snap.exists())
      return snap.data();

    return null;
  }



  public async readWithWhere({ path, keys, whereList }: {
    path: any, keys: any, whereList: RequestFilterCondition[]
  }): Promise<any> {
    const filters: QueryConstraint[] = []
    whereList.forEach(condition => filters.push(where(condition.fieldName, condition.operator, condition.value)))
    const q = whereList.length > 0 ?
      query(collection(this._firestore, this.compilePathToString(path, keys)), ...filters) :
      collection(this._firestore, this.compilePathToString(path, keys));

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log('No matching documents.');
      return;
    }

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  }

  public async write({ path, data, keys, options }): Promise<void> {
    let ref = doc(this._firestore, ...this.compilePath(path, keys));
    await setDoc(ref, data, { ...this._config.defaultWriteOptions, ...options });
  }
}
